"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth-react";
import { FaInfoCircle } from "react-icons/fa";
import { toast } from "@/lib/notify";

import SettingSail from "@/components/Pages/Packages/Cards/SettingSail";
import CatchingTheWind from "@/components/Pages/Packages/Cards/CatchingTheWind";
import FullCompass from "@/components/Pages/Packages/Cards/FullCompass";
import PackageLoginModal from "@/components/Pages/Packages/PackageLoginModal";

interface Package {
  id: string;
  name: string;
  hours: number;
  expiryDays: number;
  price: number;
  pricePerHour: number;
  features: string[];
}

export default function PackagesContent({ initialPackages }: { initialPackages: Package[] }) {
  const { data: session } = useSession();
  const router = useRouter();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const packageOrder = ["The Spark", "The Glow", "The Blaze"];

  const sortedPackages = [...initialPackages].sort(
    (a, b) => packageOrder.indexOf(a.name) - packageOrder.indexOf(b.name),
  );

  const handlePackageSelection = (packageId: string) => {
    if (
      session?.user?.role === "ADMIN" ||
      session?.user?.role === "OPS_MANAGER"
    ) {
      // Staff can't buy packages — bounce them with an explanation
      // instead of dropping them on /checkout where the role guard
      // would silently no-op.
      toast.info("Staff accounts can't purchase packages.");
      return;
    }
    if (session?.user) {
      router.push(`/checkout?id=${packageId}`);
    } else {
      setSelectedPackageId(packageId);
      setShowLoginModal(true);
    }
  };

  // After OAuth completes, the backend redirects to the callbackUrl we
  // pass to the modal — so there's no in-process success handler here.

  const scrollToPackages = () => {
    const packagesSection = document.getElementById("learning-packages");
    if (packagesSection) {
      packagesSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (session?.user?.role === "TEACHER") {
      router.push("/dashboard");
    }
  }, [session?.user?.role, router]);

  return (
    <div className="overflow-hidden">
      <div>
        <div className="bg-white">
          {/* Hero Section */}
          <section className="bg-[#ef7e74]">
            <div className="rounded-b-semifull bg-[#E9E9EA] pb-8 sm:pb-10 md:pb-14 lg:pb-16">
              <div className="rounded-b-semifull relative bg-[#27335d] py-10 text-white md:py-16">
                <div className="mx-auto flex flex-row items-center justify-center md:mt-10">
                  <div className="text-start">
                    <p className="xsm:text-xl text-xl font-bold sm:text-2xl md:text-5xl lg:text-6xl xl:text-7xl">
                      Flexible Packages for Every
                      <span className="block"> Goal and Budget</span>
                    </p>
                  </div>
                </div>
                <div className="xsm:my-5 xsm:pb-3 my-5 flex flex-col items-center justify-center pb-3 sm:my-8 sm:pb-5 md:my-10 md:pb-8 lg:my-14 lg:pb-14 xl:my-16 xl:pb-16">
                  <p className="xsm:text-sm xsm:font-medium w-full text-center text-xs font-semibold text-white sm:text-lg sm:font-medium md:text-xl md:font-bold lg:text-2xl lg:font-semibold xl:text-3xl xl:font-bold">
                    Whether you&apos;re just starting or ready to dive{" "}
                    <span className="block md:inline">
                      deep, we have a{" "}
                      <span className="md:block lg:inline">
                        package{" "}
                        <span className="xl:block">
                          for{" "}
                          <span className="md:inline-block lg:block xl:inline">
                            you!
                          </span>
                        </span>
                      </span>
                    </span>
                  </p>
                </div>
                <div className="xsm:bottom-[29px] absolute bottom-[34px] left-1/2 -translate-x-1/2 translate-y-1/2 transform sm:bottom-[37px] md:right-[2%] md:bottom-[49px] md:left-auto lg:bottom-[59px] xl:right-[10%] 2xl:right-[27%]">
                  <Image
                    src="/images/svg/clickcheck2.svg"
                    alt="clickcheck"
                    width={100}
                    height={100}
                    className="xsm:w-24 z-20 h-auto w-24 object-contain sm:w-[120px] md:w-40 lg:w-48"
                  />
                </div>
              </div>
            </div>
          </section>
          {/* Learning Packages Section */}
          <>
            <section id="learning-packages" className="bg-[#27335d]">
              <div className="rounded-b-semifull xsm:pb-7 bg-[#E9E9EA] pb-7 sm:pb-9 md:pb-24 lg:pb-32">
                <div className="rounded-b-semifull xsm:py-6 bg-[#ef7e74] py-6 text-white sm:py-8 md:py-16">
                  {/* Intro */}
                  <div className="text-center lg:mt-16">
                    <h1 className="xsm:text-2xl text-2xl font-bold tracking-tight text-[#27335D] sm:text-3xl md:text-5xl lg:text-6xl">
                      Pick Your Perfect
                    </h1>
                    <h1 className="xsm:text-2xl text-2xl font-bold tracking-tight text-[#27335D] sm:text-3xl md:text-5xl lg:text-6xl">
                      Learning Package!
                    </h1>
                  </div>
                  {/* 55-minute lesson note */}
                  <p
                    data-testid="packagesLessonDurationNote"
                    className="mx-auto mt-6 flex items-center justify-center gap-1.5 text-xs text-white sm:text-sm"
                  >
                    <FaInfoCircle className="h-3 w-3 shrink-0" />
                    Each hour of credit is a 55-minute lesson, with a 5-minute buffer between sessions.
                  </p>
                  {/* Packages */}
                  <div className="xsm:mb-8 xsm:mt-12 mt-12 mb-0.5 flex flex-col items-center justify-center gap-8 sm:mt-16 sm:mb-12 md:mt-20 md:mb-8 lg:mt-24 lg:mb-12 lg:flex-row lg:items-end lg:gap-2">
                    {sortedPackages.map((pkg) => (
                      <div key={pkg.id}>
                        {pkg.name === "The Spark" && (
                          <SettingSail
                            packageData={pkg}
                            onSelectPackage={handlePackageSelection}
                          />
                        )}
                        {pkg.name === "The Glow" && (
                          <CatchingTheWind
                            packageData={pkg}
                            onSelectPackage={handlePackageSelection}
                          />
                        )}
                        {pkg.name === "The Blaze" && (
                          <FullCompass
                            packageData={pkg}
                            onSelectPackage={handlePackageSelection}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Login Modal */}
            <PackageLoginModal
              isOpen={showLoginModal}
              closeModal={() => setShowLoginModal(false)}
              callbackUrl={
                selectedPackageId
                  ? `/checkout?id=${selectedPackageId}`
                  : "/packages"
              }
            />
          </>
          {/* Why Choose Package Section */}
          <section className="bg-[#4887f5]">
            <div className="rounded-b-semifull xsm:pb-7 bg-[#E9E9EA] pb-7 sm:pb-9 md:pb-24 lg:pb-32">
              <div className="rounded-b-semifull xsm:pb-9 xsm:pt-2 bg-[#27335d] text-white sm:pt-3 sm:pb-8 md:pt-10 md:pb-20 lg:pt-12 lg:pb-24 xl:pt-16 xl:pb-28">
                <div className="xsm:mb-11 xsm:max-w-[90%] mx-auto mt-10 mb-11 w-full max-w-[90%] sm:mb-12 md:mb-16 md:max-w-[85%] lg:max-w-[80%]">
                  <div>
                    <p className="xsm:text-[1.4rem] text-[1.4rem] font-bold sm:text-[1.5rem] md:text-[2.5rem] lg:text-[3.5rem] xl:text-[4rem]">
                      Why Choose a Package?
                    </p>
                  </div>
                </div>
                {/* Content */}
                <div className="mx-auto w-[80%] space-y-5 md:w-[52%] md:space-y-7 lg:w-[43%] xl:w-max">
                  <p className="rounded-xl bg-[#FCE8BD] py-3 text-center text-[1.05rem] font-bold text-[#27335D] md:border-2 md:border-[#F7C65A] md:px-5 md:py-4 md:text-start md:text-[1.1rem] lg:px-7 lg:py-5 lg:text-[1.2rem] xl:px-9 xl:py-6 xl:text-2xl">
                    Structured Plans
                  </p>
                  <p className="rounded-xl bg-[#F9D17B] py-3 text-center text-[1.05rem] font-bold text-[#27335D] md:border-2 md:border-[#F7C65A] md:px-5 md:py-4 md:text-start md:text-[1.1rem] lg:px-7 lg:py-5 lg:text-[1.2rem] xl:px-9 xl:py-6 xl:text-2xl">
                    Consistent Learning
                  </p>
                  <p className="rounded-xl bg-[#F7C65A] py-3 text-center text-[1.05rem] font-bold text-[#27335D] md:border-2 md:border-[#FCE8BD] md:px-5 md:py-4 md:text-start md:text-[1.1rem] lg:px-7 lg:py-5 lg:text-[1.2rem] xl:px-9 xl:py-6 xl:text-2xl">
                    Better Value
                    <span className="hidden md:inline">
                      {" "}
                      compared to one sessions
                    </span>
                  </p>
                </div>
                <div className="flex flex-row justify-center">
                  <button
                    data-testid="packagesStartNowButton"
                    onClick={scrollToPackages}
                    className="xsm:px-11 mt-7 transform-gpu rounded-full border-b-8 border-black bg-[#4887F5] px-11 py-3.5 text-[1.2rem] font-bold text-[#E9E9EA] shadow-2xl transition-all duration-300 ease-out hover:bg-[#3666b9] active:scale-95 sm:px-12 md:mt-14 md:px-20 md:py-3.5 md:text-[1.35rem] md:text-[#27335d] xl:mt-16 xl:px-24 xl:py-5 xl:text-3xl"
                  >
                    Start now!
                  </button>
                </div>
                <p className="mt-12 hidden text-center text-xl font-bold md:block lg:text-2xl xl:text-3xl">
                  The right plan makes all the difference commit to your goals
                  today!
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
